import React, { useState, useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Fab, Tooltip, IconButton } from '@material-ui/core';
import { Add, Refresh } from '@material-ui/icons';
import { Redirect } from 'react-router-dom';
import { createHistory, BackButton, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';

import MaterialTable from 'material-table';
import { hasPermission } from '../../sc-utilities';
import NotificationSettingDelete from './NotificationSettingDelete';
import Toolbar from '../../sc-utilities/Toolbar';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        }
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const NotificationSettingList = props => {
    const { deleteModalState, deleting, tableActions, saveColumnsFilter, classes, onFetchNotificationSettings, onFetchNotificationSettingsCount, translations, activeCustomer, permissions, tableColumns, fetching, handleDelete, tableRef } = props;
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['notification_setting_delete']),
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        search: false,
        minBodyHeight: window.innerHeight - 198 < 300 ? 300 : window.innerHeight - 198,
        maxBodyHeight: window.innerHeight - 198,
        pageSizeOptions: rowsPageSizeInit,

        exportButton: false,
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchNotificationSettingsCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchNotificationSettings(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, notificationSettingID: null, redirectPath: '' });
    const redirectHandler = (path, notificationSettingID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, notificationSettingID: notificationSettingID, redirectPath: path })
    };

    const handleCloseDanger = async () => {
        if (deleteModalState.forBulk) {
            tableRef.current && tableRef.current.onQueryChange()
            await props.bulkDeleteNotificationSettings({ NotificationSettingIDs: deleteModalState.notificationSettingIDs, ActiveCustomerID: activeCustomer });
            props.handleDelete();
            return;
        }
        tableRef.current && tableRef.current.onQueryChange()
        await props.deleteNotificationSetting({ NotificationSettingID: deleteModalState.notificationSettingID, ActiveCustomerID: activeCustomer });
        props.handleDelete();
    };

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['notification_settings'] || 'Notification settings'}</Typography>
                {hasPermission(permissions, 'notification_setting_create') ? <Fab
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/settings/notificationSetting/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`NotificationSetting`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }
    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}     
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}       
                />
            {
                hasPermission(permissions, 'notification_setting_delete') ? (
                    <NotificationSettingDelete
                        activeCustomer={activeCustomer}
                        deleting={deleting}
                        entityName={translations['notification_settings'] || 'Notification settings'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        deleteModalState={deleteModalState}
                        handleDelete={handleDelete}
                    />
                ) : null
            }
        </Paper >
    );
};

export default withStyles(styles, { withTheme: true })(NotificationSettingList);